import type { SafeVersion, TransactionOptions } from '@safe-global/types-kit'
import { type TransactionResponse, type Eip1193Provider, type Provider, type BrowserProvider } from 'ethers'
import semverSatisfies from 'semver/functions/satisfies'
import { type SafeState, cgwApi as safesApi } from '@safe-global/store/gateway/AUTO_GENERATED/safes'
import { cgwApi as relayApi } from '@safe-global/store/gateway/AUTO_GENERATED/relay'
import { type Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
import { getStoreInstance } from '@/store'
import type { UrlObject } from 'url'
import { AppRoutes } from '@/config/routes'
import { withSpaceId, withSpaceIdInUrl } from '@/hooks/useUrlSpaceId'
import { SAFE_APPS_EVENTS, trackEvent } from '@/services/analytics'
import Safe from '@safe-global/protocol-kit'
import type { PredictedSafeProps } from '@safe-global/protocol-kit'

import { backOff } from 'exponential-backoff'
import { EMPTY_DATA, ZERO_ADDRESS } from '@safe-global/utils/utils/constants'
import {
  getCompatibilityFallbackHandlerDeployment,
  getCompatibilityFallbackHandlerDeployments,
  getProxyFactoryDeployment,
  getProxyFactoryDeployments,
  getSafeL2SingletonDeployments,
  getSafeSingletonDeployments,
  getSafeToL2SetupDeployments,
} from '@safe-global/safe-deployments'
import { ECOSYSTEM_ID_ADDRESS } from '@/config/constants'
import type { ReplayedSafeProps, UndeployedSafeProps } from '@safe-global/utils/features/counterfactual/store/types'
import { isPredictedSafeProps } from '@/features/counterfactual/services'
import {
  getSafeContractDeployment,
  getChainAgnosticAddress,
  getSafeToL2SetupVersion,
} from '@safe-global/utils/services/contracts/deployments'
import {
  Safe__factory,
  Safe_proxy_factory__factory,
  Safe_to_l2_setup__factory,
} from '@safe-global/utils/types/contracts'
import { createWeb3 } from '@/hooks/wallets/web3'
import { hasMultiChainCreationFeatures } from '@/features/multichain'

// Type for the lazy-loaded activateReplayedSafe function
export type ActivateReplayedSafeFn = (
  chain: Chain,
  props: ReplayedSafeProps,
  provider: BrowserProvider,
  options: TransactionOptions,
) => Promise<TransactionResponse>

export type SafeCreationProps = {
  owners: string[]
  threshold: number
  saltNonce: number
}

/**
 * Create a Safe creation transaction via Core SDK and submits it to the wallet
 *
 * @param activateReplayedSafe - Optional function for activating replayed safes (lazy-loaded from counterfactual feature)
 */
export const createNewSafe = async (
  provider: Eip1193Provider,
  undeployedSafeProps: UndeployedSafeProps,
  chain: Chain,
  options: TransactionOptions,
  callback: (txHash: string) => void,
  isL1SafeSingleton?: boolean,
  activateReplayedSafe?: ActivateReplayedSafeFn,
): Promise<void> => {
  let txResponse: TransactionResponse
  if (isPredictedSafeProps(undeployedSafeProps)) {
    const safe = await Safe.init({
      predictedSafe: undeployedSafeProps,
      provider,
      isL1SafeSingleton,
    })

    const creationTx = await safe.createSafeDeploymentTransaction()

    const signer = await createWeb3(provider).getSigner()

    txResponse = await signer?.sendTransaction({
      ...creationTx,
      ...options,
    })
  } else {
    if (!activateReplayedSafe) {
      throw new Error('activateReplayedSafe function is required for replayed safes')
    }
    txResponse = await activateReplayedSafe(chain, undeployedSafeProps, createWeb3(provider), options)
  }
  callback(txResponse.hash)
}

export const encodeSafeSetupCall = (safeAccountConfig: ReplayedSafeProps['safeAccountConfig']) => {
  return Safe__factory.createInterface().encodeFunctionData('setup', [
    safeAccountConfig.owners,
    safeAccountConfig.threshold,
    safeAccountConfig.to,
    safeAccountConfig.data,
    safeAccountConfig.fallbackHandler,
    ZERO_ADDRESS,
    0,
    safeAccountConfig.paymentReceiver,
  ])
}

/**
 * Encode a Safe creation transaction NOT using the Core SDK because it doesn't support that
 * This is used for gas estimation.
 */
export const encodeSafeCreationTx = (undeployedSafe: UndeployedSafeProps, chain: Chain) => {
  const replayedSafeProps = assertNewUndeployedSafeProps(undeployedSafe, chain)

  return Safe_proxy_factory__factory.createInterface().encodeFunctionData('createProxyWithNonce', [
    replayedSafeProps.masterCopy,
    encodeSafeSetupCall(replayedSafeProps.safeAccountConfig),
    BigInt(replayedSafeProps.saltNonce),
  ])
}

export const estimateSafeCreationGas = async (
  chain: Chain,
  provider: Provider,
  from: string,
  undeployedSafe: UndeployedSafeProps,
): Promise<bigint> => {
  // Estimate against the factory the creation will actually be sent to (see
  // activateReplayedSafe/relaySafeCreation) — a version-derived factory can differ
  // from the replayed creation's factory and deploys different proxy bytecode,
  // underestimating the gas and making the real transaction run out of gas.
  const replayedSafeProps = assertNewUndeployedSafeProps(undeployedSafe, chain)
  const encodedSafeCreationTx = encodeSafeCreationTx(replayedSafeProps, chain)

  const gas = await provider.estimateGas({
    from,
    to: replayedSafeProps.factoryAddress,
    data: encodedSafeCreationTx,
  })

  return gas
}

/**
 * Poll for safe info after creation until the safe is indexed by client-gateway
 * Uses RTK Query with exponential backoff retry (19 attempts over ~4 minutes)
 */
export const pollSafeInfo = async (chainId: string, safeAddress: string): Promise<SafeState> => {
  const store = getStoreInstance()

  // Use exponential backoff to retry RTK Query calls
  return backOff(
    async () => {
      const queryAction = safesApi.endpoints.safesGetSafeV1.initiate(
        { chainId, safeAddress },
        {
          subscribe: false,
          forceRefetch: true,
        },
      )

      const queryPromise = store.dispatch(queryAction)
      try {
        const result = await queryPromise.unwrap()
        return result
      } finally {
        queryPromise.unsubscribe()
      }
    },
    {
      startingDelay: 750,
      maxDelay: 20000,
      numOfAttempts: 19,
      retry: (e) => {
        console.info('waiting for client-gateway to provide safe information', e)
        return true
      },
    },
  )
}

/** The page after a Safe is deployed, in the Workspace `spaceId` (null: outside any Workspace). */
export const getRedirect = (
  chainPrefix: string,
  safeAddress: string,
  spaceId: string | null,
  redirectQuery?: string | string[],
): UrlObject | string => {
  const redirectUrl = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery
  const address = `${chainPrefix}:${safeAddress}`

  // Should never happen in practice
  if (!chainPrefix) return AppRoutes.index

  // Go to the dashboard if no specific redirect is provided
  if (!redirectUrl || !redirectUrl.startsWith(AppRoutes.apps.index)) {
    return { pathname: AppRoutes.home, query: withSpaceId({ safe: address }, spaceId) }
  }

  // Otherwise, redirect to the provided URL (e.g. from a Safe App)

  // Track the redirect to Safe App
  trackEvent(SAFE_APPS_EVENTS.SHARED_APP_OPEN_AFTER_SAFE_CREATION)

  // We're prepending the safe address directly here because the `router.push` doesn't parse
  // The URL for already existing query params
  // TODO: Check if we can accomplish this with URLSearchParams or URL instead
  const hasQueryParams = redirectUrl.includes('?')
  const appendChar = hasQueryParams ? '&' : '?'
  return withSpaceIdInUrl(redirectUrl + `${appendChar}safe=${address}`, spaceId)
}

export const relaySafeCreation = async (chain: Chain, undeployedSafeProps: UndeployedSafeProps) => {
  const store = getStoreInstance()

  const replayedSafeProps = assertNewUndeployedSafeProps(undeployedSafeProps, chain)
  const encodedSafeCreationTx = encodeSafeCreationTx(replayedSafeProps, chain)

  const relayAction = relayApi.endpoints.relayRelayV1.initiate({
    chainId: chain.chainId,
    relayDto: {
      to: replayedSafeProps.factoryAddress,
      data: encodedSafeCreationTx,
      version: replayedSafeProps.safeVersion,
    },
  })

  const relayResponse = await store.dispatch(relayAction).unwrap()
  return relayResponse.taskId
}

export type UndeployedSafeWithoutSalt = Omit<ReplayedSafeProps, 'saltNonce'>

/**
 * Creates a new undeployed Safe without default config:
 *
 * Always use the L1 MasterCopy and add a migration to L2 in to the setup.
 * Use our ecosystem ID as paymentReceiver.
 *
 */
export const createNewUndeployedSafeWithoutSalt = (
  safeVersion: SafeVersion,
  safeAccountConfig: Pick<ReplayedSafeProps['safeAccountConfig'], 'owners' | 'threshold'> & {
    paymentReceiver?: string
  },
  chain: Chain,
): UndeployedSafeWithoutSalt => {
  // Resolve contract addresses (per-chain for registered chains, chain-agnostic fallback for new chains)
  const deploymentType = chain.zk ? 'zksync' : 'canonical'

  const fallbackHandlerDeployments = getCompatibilityFallbackHandlerDeployments({ version: safeVersion })
  const fallbackHandlerAddress = getChainAgnosticAddress(fallbackHandlerDeployments, chain.chainId, deploymentType)

  const safeL2Deployments = getSafeL2SingletonDeployments({ version: safeVersion })
  const safeL2Address = getChainAgnosticAddress(safeL2Deployments, chain.chainId, deploymentType)

  const safeL1Deployments = getSafeSingletonDeployments({ version: safeVersion })
  const safeL1Address = getChainAgnosticAddress(safeL1Deployments, chain.chainId, deploymentType)

  const safeFactoryDeployments = getProxyFactoryDeployments({ version: safeVersion })
  const safeFactoryAddress = getChainAgnosticAddress(safeFactoryDeployments, chain.chainId, deploymentType)

  if (!safeL2Address || !safeL1Address || !safeFactoryAddress || !fallbackHandlerAddress) {
    throw new Error('No Safe deployment found')
  }

  const safeToL2SetupDeployments = getSafeToL2SetupDeployments({ version: getSafeToL2SetupVersion(safeVersion) })
  const safeToL2SetupAddress = getChainAgnosticAddress(safeToL2SetupDeployments, chain.chainId, deploymentType)
  const safeToL2SetupInterface = Safe_to_l2_setup__factory.createInterface()

  // Only do migration if the chain supports multiChain deployments and has a SafeToL2Setup deployment
  const includeMigration =
    hasMultiChainCreationFeatures(chain) && semverSatisfies(safeVersion, '>=1.4.1') && Boolean(safeToL2SetupAddress)

  const masterCopy = includeMigration ? safeL1Address : chain.l2 ? safeL2Address : safeL1Address

  const replayedSafe: Omit<ReplayedSafeProps, 'saltNonce'> = {
    factoryAddress: safeFactoryAddress,
    masterCopy,
    safeAccountConfig: {
      threshold: safeAccountConfig.threshold,
      owners: safeAccountConfig.owners,
      fallbackHandler: fallbackHandlerAddress,
      to: includeMigration && safeToL2SetupAddress ? safeToL2SetupAddress : ZERO_ADDRESS,
      data: includeMigration ? safeToL2SetupInterface.encodeFunctionData('setupToL2', [safeL2Address]) : EMPTY_DATA,
      paymentReceiver: safeAccountConfig.paymentReceiver ?? ECOSYSTEM_ID_ADDRESS,
    },
    safeVersion,
  }

  return replayedSafe
}

/**
 * Migrates a counterfactual Safe from the pre multichain era to the new predicted Safe data
 * @param predictedSafeProps
 * @param chain
 * @returns
 */
export const migrateLegacySafeProps = (predictedSafeProps: PredictedSafeProps, chain: Chain): ReplayedSafeProps => {
  const safeVersion = predictedSafeProps.safeDeploymentConfig?.safeVersion
  const saltNonce = predictedSafeProps.safeDeploymentConfig?.saltNonce
  const { chainId } = chain
  if (!safeVersion || !saltNonce) {
    throw new Error('Undeployed Safe with incomplete data.')
  }

  const fallbackHandlerDeployment = getCompatibilityFallbackHandlerDeployment({
    version: safeVersion,
    network: chainId,
  })
  const fallbackHandlerAddress = fallbackHandlerDeployment?.defaultAddress

  const masterCopyDeployment = getSafeContractDeployment(chain, safeVersion)
  const masterCopyAddress = masterCopyDeployment?.defaultAddress

  const safeFactoryDeployment = getProxyFactoryDeployment({ version: safeVersion, network: chainId })
  const safeFactoryAddress = safeFactoryDeployment?.defaultAddress

  if (!masterCopyAddress || !safeFactoryAddress || !fallbackHandlerAddress) {
    throw new Error('No Safe deployment found')
  }

  return {
    factoryAddress: safeFactoryAddress,
    masterCopy: masterCopyAddress,
    safeAccountConfig: {
      threshold: predictedSafeProps.safeAccountConfig.threshold,
      owners: predictedSafeProps.safeAccountConfig.owners,
      fallbackHandler: predictedSafeProps.safeAccountConfig.fallbackHandler ?? fallbackHandlerAddress,
      to: predictedSafeProps.safeAccountConfig.to ?? ZERO_ADDRESS,
      data: predictedSafeProps.safeAccountConfig.data ?? EMPTY_DATA,
      paymentReceiver: predictedSafeProps.safeAccountConfig.paymentReceiver ?? ZERO_ADDRESS,
    },
    safeVersion,
    saltNonce,
  }
}

export const assertNewUndeployedSafeProps = (props: UndeployedSafeProps, chain: Chain): ReplayedSafeProps => {
  if (isPredictedSafeProps(props)) {
    return migrateLegacySafeProps(props, chain)
  }

  return props
}
